import { instance } from "apis/interceptor";
import { TMember } from "types/user.types";

type ResponseType<T> = {
  success: boolean;
  message?: string;
  data?: T;
};

export type TIntegrationCategory = "DOMAIN" | "ORG_UNIT";

//* INFO: 멤버 연동 생성
export const createMemberIntegration = async (
  integrationCategory: TIntegrationCategory,
  googleRefreshToken: string,
  acceptTerm: boolean,
  domains: string[],
  orgUnits: string[]
) => {
  try {
    const res = await instance.post("/member-integration", {
      integrationCategory,
      googleRefreshToken,
      acceptTerm,
      domains,
      orgUnits,
    });

    return {
      success: res.data.id ? true : false,
    };
  } catch (error) {
    return {
      success: false,
      message: `${error}`,
    };
  }
};

//* INFO: 멤버 연동 해제
export const unlinkMemberIntegration = async (): Promise<ResponseType<boolean>> => {
  try {
    const res = await instance.delete("/member-integration");
    return { success: true, data: res.data };
  } catch (error: any) {
    return { success: false };
  }
};

//* INFO: 멤버 연동 정보(채널) 수정
export const updateMemberIntegrationChannel = async (channel: string): Promise<ResponseType<boolean>> => {
  try {
    const res = await instance.patch("/member-integration/channel", {
      alarmChannel: channel,
    });
    return { success: true, data: res.data.result };
  } catch (error: any) {
    return { success: false };
  }
};

export type TMemberIntegrationResponse = {
  id: string;
  createdAt: string;
  updateAt: string;
  integrationProvider: "GOOGLE";
  integrationCategory: TIntegrationCategory;
  acceptTerm: boolean;
  domains: string[];
  orgUnits: string[];
  alarmChannel: string;
  lastSyncedAt: string;
  syncedCreatedNum: number;
  syncedDeletedNum: number;
  integratedById: TMember;
};

//* INFO: 멤버 연동 데이터 조회
export const getMemberIntegration = async (): Promise<ResponseType<TMemberIntegrationResponse>> => {
  try {
    const res = await instance.get("/member-integration");
    return {
      success: true,
      data: res.data,
    };
  } catch (error) {
    return {
      success: false,
      message: `${error}`,
    };
  }
};

export type TMemberChange = {
  added: { email: string; name?: string }[];
  toBeRemoved: { email: string; name?: string }[];
};

//* INFO: 멤버 변경사항 조회
export const getMemberChange = async (): Promise<ResponseType<TMemberChange>> => {
  try {
    const res = await instance.get("/member-integration/changes");
    return { success: true, data: res.data };
  } catch (error: any) {
    return { success: false };
  }
};

//* INFO: 멤버 동기화
export const syncMember = async (): Promise<ResponseType<boolean>> => {
  try {
    const res = await instance.post("auth/member-integration/sync");
    return { success: true, data: res.data };
  } catch (error: any) {
    return { success: false };
  }
};

//* INFO: 멤버 동기화 (메신저 전용)
export const syncMemberForMessenger = async (memberPublicId: string): Promise<ResponseType<boolean>> => {
  try {
    const res = await instance.post("auth/member-integration/sync/by-messenger", {
      memberPublicId,
      channel: "Web",
    });
    return { success: true, data: res.data };
  } catch (error: any) {
    return { success: false };
  }
};

//* INFO: GWS 도메인 조회
export const getGwsDomainList = async (): Promise<ResponseType<string[]>> => {
  try {
    const res = await instance.get("/member-integration/gws-domain");
    return { success: true, data: res.data.domains };
  } catch (error: any) {
    return {
      success: false,
      message: "Google Workspace 도메인 목록을 불러올 수 있는 권한이 없습니다. 연동을 해제한 뒤 다시 연동해 주세요.",
    };
  }
};

//* INFO: GWS 조직 단위 조회
export const getGwsOrgUnitList = async (): Promise<ResponseType<string[]>> => {
  try {
    const res = await instance.get("/member-integration/gws-org-unit");
    return { success: true, data: res.data.map((item: any) => item.orgUnitPath) };
  } catch (error: any) {
    return {
      success: false,
      message: "Google Workspace 조직 목록을 불러올 수 있는 권한이 없습니다. 연동을 해제한 뒤 다시 연동해 주세요.",
    };
  }
};

//* INFO: GWS 멤버 연동 수정
export const updateMemberIntegration = async (
  id: string,
  integrationCategory: TIntegrationCategory,
  acceptTerm: boolean,
  domains: string[],
  orgUnits: string[]
) => {
  try {
    const res = await instance.patch("/member-integration", {
      id,
      integrationCategory,
      acceptTerm,
      domains: integrationCategory === "DOMAIN" ? domains : [],
      orgUnits: integrationCategory === "ORG_UNIT" ? orgUnits : [],
    });

    return {
      success: res.data.result,
    };
  } catch (error) {
    return {
      success: false,
      message: `${error}`,
    };
  }
};
